/**
 * Export Service
 *
 * Handles exporting projects to DOCX and PDF formats.
 */

import { Document, Packer, Paragraph, HeadingLevel, TextRun } from 'docx'
import { saveAs } from 'file-saver'
import type { ProjectWithChapters, ExportDocument } from '@/types/writingStudio'

/**
 * Converts a project to the export document format
 */
export function projectToExportDocument(project: ProjectWithChapters): ExportDocument {
  return {
    title: project.project.title,
    chapters: project.chapters
      .sort((a, b) => a.order - b.order)
      .map((chapter) => ({
        title: chapter.title,
        contentPlaintext: chapter.content,
      })),
    exportedAt: new Date().toISOString(),
  }
}

/**
 * Generates a safe filename from a project title
 */
function generateFilename(title: string, extension: string): string {
  const safeName = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
  return `${safeName || 'project'}.${extension}`
}

/**
 * Exports project to DOCX format
 */
export async function exportToDocx(project: ProjectWithChapters): Promise<void> {
  const exportDoc = projectToExportDocument(project)

  // Create document sections
  const children: Paragraph[] = [
    // Title
    new Paragraph({
      text: exportDoc.title,
      heading: HeadingLevel.TITLE,
      spacing: { after: 400 },
    }),
  ]

  // Add each chapter
  for (const chapter of exportDoc.chapters) {
    // Chapter heading
    children.push(
      new Paragraph({
        text: chapter.title,
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 },
      })
    )

    // Chapter content - split by paragraphs
    const paragraphs = chapter.contentPlaintext.split(/\n\n+/)
    for (const para of paragraphs) {
      if (para.trim()) {
        children.push(
          new Paragraph({
            children: [new TextRun(para.trim())],
            spacing: { after: 200 },
          })
        )
      }
    }
  }

  // Add footer with export info
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `\n\nExported from Inkwell Jr. on ${new Date().toLocaleDateString()}`,
          italics: true,
          size: 20, // 10pt
        }),
      ],
      spacing: { before: 600 },
    })
  )

  const doc = new Document({
    sections: [
      {
        properties: {},
        children,
      },
    ],
  })

  // Generate and save the file
  const blob = await Packer.toBlob(doc)
  saveAs(blob, generateFilename(exportDoc.title, 'docx'))
}

/**
 * Exports project to PDF via browser print
 * Opens a new window with formatted content for printing
 */
export function exportToPdf(project: ProjectWithChapters): void {
  const exportDoc = projectToExportDocument(project)

  // Create HTML content for printing
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(exportDoc.title)}</title>
        <style>
          @page {
            margin: 1in;
          }
          body {
            font-family: Georgia, 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.6;
            color: #333;
            max-width: 6.5in;
            margin: 0 auto;
            padding: 0.5in;
          }
          h1 {
            font-size: 24pt;
            text-align: center;
            margin-bottom: 0.5in;
            color: #1a1a1a;
          }
          h2 {
            font-size: 16pt;
            margin-top: 0.5in;
            margin-bottom: 0.25in;
            color: #2a2a2a;
            page-break-after: avoid;
          }
          p {
            margin-bottom: 1em;
            text-indent: 0.25in;
          }
          p:first-of-type {
            text-indent: 0;
          }
          .footer {
            margin-top: 1in;
            font-style: italic;
            font-size: 10pt;
            color: #666;
            text-align: center;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(exportDoc.title)}</h1>
        ${exportDoc.chapters
          .map(
            (chapter) => `
          <h2>${escapeHtml(chapter.title)}</h2>
          ${chapter.contentPlaintext
            .split(/\n\n+/)
            .filter((p) => p.trim())
            .map((p) => `<p>${escapeHtml(p.trim())}</p>`)
            .join('\n')}
        `
          )
          .join('\n')}
        <p class="footer">Exported from Inkwell Jr. on ${new Date().toLocaleDateString()}</p>
      </body>
    </html>
  `

  // Open print window
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(html)
    printWindow.document.close()
    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.print()
    }
  }
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Export format options
 */
export type ExportFormat = 'docx' | 'pdf'

/**
 * Main export function that handles both formats
 */
export async function exportProject(
  project: ProjectWithChapters,
  format: ExportFormat
): Promise<void> {
  switch (format) {
    case 'docx':
      await exportToDocx(project)
      break
    case 'pdf':
      exportToPdf(project)
      break
  }
}
